import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import axios from "axios";
import {
  MeApi,
  MeDetailsApi,
  MeVerificationsApi,
  type V2MePasswordPutRequest,
  type V2MePatchRequest,
} from "mws-ts-v2-sdk";

let meApi: MeApi;
let meDetailsApi: MeDetailsApi;
let catalogMeApi: MeApi;
let v2MeVerificationsApi: MeVerificationsApi;

const getMeApi = () => {
  // reduce memory overhead by only using one instance
  if (!meApi) {
    meApi = new MeApi(
      undefined,
      useRuntimeConfig().public.identityServiceUrl,
      axios,
    );
  }

  return meApi;
};

const getMeDetailsApi = () => {
  // reduce memory overhead by only using one instance
  if (!meDetailsApi) {
    meDetailsApi = new MeDetailsApi(
      undefined,
      useRuntimeConfig().public.identityServiceUrl,
      axios,
    );
  }

  return meDetailsApi;
};

const getCatalogMeDetailsApi = () => {
  // reduce memory overhead by only using one instance
  if (!catalogMeApi) {
    catalogMeApi = new MeApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return catalogMeApi;
};

const getMeVerificationsApi = () => {
  // reduce memory overhead by only using one instance
  if (!v2MeVerificationsApi) {
    v2MeVerificationsApi = new MeVerificationsApi(
      undefined,
      useRuntimeConfig().public.identityServiceUrl,
      axios,
    );
  }

  return v2MeVerificationsApi;
};

export const meQueryKeys = createQueryKeys("me", {
  verifications: (params?: { token?: string }) => ({
    queryKey: ["list"],
    queryFn: async () => {
      const response = await getMeVerificationsApi().v2MeVerificationsGet({
        params,
      });

      return response.data;
    },
  }),
  details: () => ({
    queryKey: ["details"],
    queryFn: async () => {
      const response = await getMeDetailsApi().v2MeDetailsGet();

      return response.data;
    },
  }),
  /**
   * @deprecated This endpoint is deprecated from the backend due to performance reasons
   * Use `meQueryKeys.details()` or the `Bidder` entity call instead
   */
  detail: () => ({
    queryKey: ["detail"],
    queryFn: async () => {
      const response = await getMeApi().v2MeGet();

      return response.data;
    },
  }),
  bidder: () => ({
    queryKey: ["bidder"],
    queryFn: async () => {
      const response = await getCatalogMeDetailsApi().v2MeBidderGet();

      return response.data;
    },
  }),
});

export const useInfiniteMyBidsQuery = (
  pageSize: MaybeRefOrGetter<number>,
  tab: MaybeRef<"all" | "live" | "won" | "closed">,
) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  return useInfiniteQuery({
    queryKey: ["me", "bids", "infiniteList", tab],
    queryFn: async ({ pageParam }) => {
      const response = await getCatalogMeDetailsApi().v2MeBidsGet({
        xPlatformKey: unref(platformId),
        pageSize: toValue(pageSize),
        offset: pageParam,
        publicBidsStatusFilters: unref(tab),
      });

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (toValue(pageSize) || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
    select: (data) => {
      const flattenedBidsList =
        data.pages.flatMap((page) => page.results) ?? [];

      return {
        data: flattenedBidsList,
        totalCount: data.pages?.[0]?.totalCount ?? 0,
      };
    },
  });
};

export const useUpdateMeMutation = () => {
  const mutationFn = (userDetailsRequest: V2MePatchRequest | undefined) =>
    getMeApi().v2MePatch({ v2MePatchRequest: userDetailsRequest });

  const queryClient = useQueryClient();
  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: meQueryKeys._def });
  };

  return useMutation({ mutationFn, onSuccess });
};

export const useMePhoneVerificationPostMutation = () =>
  useMutation({
    mutationFn: () => getMeVerificationsApi().v2MeVerificationsPhonePost(),
  });

export const useMePhoneVerificationPatchMutation = () =>
  useMutation({
    mutationFn: (token: string) =>
      getMeVerificationsApi().v2MeVerificationsPhonePatch({
        v2MeVerificationsPhonePatchRequest: { token },
      }),
  });

export const useChangePasswordMutation = () => {
  const nuxtApp = useNuxtApp();

  return useMutation({
    mutationFn: async (v2MePasswordPutRequest: V2MePasswordPutRequest) => {
      if (nuxtApp.$amplify) {
        await nuxtApp.$amplify.auth.updatePassword({
          oldPassword: v2MePasswordPutRequest.currentPassword,
          newPassword: v2MePasswordPutRequest.newPassword,
        });
      }
    },
  });
};

// Update phone number + send verification code
export const useValidatePhoneMutation = () =>
  useMutation({
    mutationFn: (phoneNumber: string) =>
      getMeApi().v2MePhonePut({ v2MePhonePutRequest: { phoneNumber } }),
  });

export const useVerifyMailTokenMutation = () =>
  useMutation({
    mutationFn: (token: string) =>
      getMeVerificationsApi().v2MeVerificationsEmailPatch({
        v2MeVerificationsEmailPatchRequest: { token },
      }),
  });

export const useResendVerifyEmailMutation = () =>
  useMutation({
    mutationFn: () => getMeVerificationsApi().v2MeVerificationsEmailPost(),
  });

export const useMeDetailsQuery = () => {
  const { isLoggedIn } = useAuth();

  return useQuery({
    ...meQueryKeys.details(),
    enabled: isLoggedIn,
  });
};

export const useMeDeleteAccountMutation = () => {
  const { t } = useI18n();
  const toast = useToast();

  return useMutation({
    mutationFn: () => getMeApi().v2MeDelete(),
    onSuccess: async () => {
      toast.success(
        t("myAccount.accountSettings.deleteAccount.successMessage"),
      );
    },
    onError: () => {
      toast.error(t("myAccount.accountSettings.deleteAccount.errorMessage"));
    },
  });
};
